/**
 * LogicCraft - Módulo de Máquinas Elétricas Industriais 3D & Eletrotécnica
 * Namespace: window.LogicCraft.machines
 *
 * Contém:
 * - Resolução topológica por Conexões de Cabos (Union-Find / Disjoint-Set)
 * - Motor de cálculo eletrotécnico do Motor Trifásico 6P (Estrela Y / Triângulo Δ)
 * - Motor de cálculo do Motor Monofásico com Capacitor (Horário, Anti-Horário e Stall)
 * - Modelagem 3D procedural e ergonômica com aletas circunferenciais e caixa de bornes superior
 */

'use strict';

window.LogicCraft = window.LogicCraft || {};

window.LogicCraft.machines = (function() {
    const state = window.LogicCraft.state;
    const core = window.LogicCraft.core;

    // Cores e Acabamentos Industriais
    const COLOR_STATOR_TRI  = 0x334155; // Cinza escuro usinado
    const COLOR_STATOR_MONO = 0x1E3A5F; // Azul escuro industrial
    const COLOR_BAKELITE    = 0x854D0E; // Placa de baquelite isolante âmbar/marrom
    const COLOR_BRASS       = 0xD97706; // Latão sextavado polido
    const COLOR_STEEL       = 0xF1F5F9; // Aço retificado cromado

    /**
     * Estrutura Disjoint-Set / Union-Find para topologia de bornes
     */
    class UnionFind {
        constructor() {
            this.parent = new Map();
        }

        find(i) {
            if (!this.parent.has(i)) this.parent.set(i, i);
            if (this.parent.get(i) !== i) {
                this.parent.set(i, this.find(this.parent.get(i)));
            }
            return this.parent.get(i);
        }

        union(i, j) {
            const rootI = this.find(i);
            const rootJ = this.find(j);
            if (rootI !== rootJ) {
                this.parent.set(rootI, rootJ);
            }
        }

        connected(i, j) {
            return this.find(i) === this.find(j);
        }
    }

    function isMachineType(type) {
        return type === core.COMP.MOTOR_INDUCTION_6P || type === core.COMP.MOTOR_INDUCTION_MONO;
    }

    function pinKey(nodeX, nodeY, nodeZ, pinId) {
        return `${core.snap(nodeX)},${core.snap(nodeY || 0)},${core.snap(nodeZ)}:${pinId}`;
    }

    /**
     * Resolução topológica em tempo real dos motores elétricos
     */
    function solvePowerCircuit(circuitMap, airWiresList) {
        const activeCircuit = circuitMap || state.circuit;
        const activeWires = airWiresList || state.airWires;
        if (!activeCircuit) return;

        const uf = new UnionFind();

        // 1. Mapeia todas as conexões de cabos (Air Wires)
        if (activeWires && activeWires.length > 0) {
            activeWires.forEach(wire => {
                if (!wire.from || !wire.to) return;
                const k1 = pinKey(wire.from.nodeX, wire.from.nodeY, wire.from.nodeZ, wire.from.pinId);
                const k2 = pinKey(wire.to.nodeX, wire.to.nodeY, wire.to.nodeZ, wire.to.pinId);
                uf.union(k1, k2);
            });
        }

        // 2. Mapeia Contatores Tripolares de Potência (CONTACTOR_3P)
        activeCircuit.forEach(node => {
            if (node.type === core.COMP.CONTACTOR_3P) {
                const cKey = core.key(node.x, node.y, node.z);
                // Quando a bobina A1/A2 recebe comando (node.state = true):
                if (node.state) {
                    // Contatos principais de força comutam fechando os circuitos L1->T1, L2->T2, L3->T3
                    const pIn1 = [`${cKey}:1/L1`, `${cKey}:1`, `${cKey}:L1`];
                    const pOut1 = [`${cKey}:2/T1`, `${cKey}:2`, `${cKey}:T1`];
                    pIn1.forEach(i => pOut1.forEach(o => uf.union(i, o)));

                    const pIn2 = [`${cKey}:3/L2`, `${cKey}:3`, `${cKey}:L2`];
                    const pOut2 = [`${cKey}:4/T2`, `${cKey}:4`, `${cKey}:T2`];
                    pIn2.forEach(i => pOut2.forEach(o => uf.union(i, o)));

                    const pIn3 = [`${cKey}:5/L3`, `${cKey}:5`, `${cKey}:L3`];
                    const pOut3 = [`${cKey}:6/T3`, `${cKey}:6`, `${cKey}:T3`];
                    pIn3.forEach(i => pOut3.forEach(o => uf.union(i, o)));

                    // Contato auxiliar integrado de selo NA 13-14
                    uf.union(`${cKey}:13`, `${cKey}:14`);
                }
            }
        });

        // 3. Localiza fontes industriais trifásicas (POWER_SUPPLY_3P)
        let activePowerSupply = null;
        activeCircuit.forEach(node => {
            if (node.type === core.COMP.POWER_SUPPLY_3P) {
                if (!activePowerSupply || node.manualValue !== false) {
                    activePowerSupply = node;
                }
            }
        });

        // 4. Avalia cada motor presente na bancada
        activeCircuit.forEach(node => {
            if (node.type === core.COMP.MOTOR_INDUCTION_6P) {
                analyseMotor6P(node, uf, activePowerSupply, activeCircuit, activeWires);
            } else if (node.type === core.COMP.MOTOR_INDUCTION_MONO) {
                analyseMotorMono(node, uf, activeCircuit, activeWires);
            }
        });
    }

    /**
     * Verifica se um borne do motor recebe linha ativa através de cabos ou conexões
     */
    function isTerminalEnergized(motorKey, pinId, customWires, customCircuit) {
        const wires = customWires || state.airWires;
        const circuit = customCircuit || state.circuit;
        if (!wires) return false;
        const target = `${motorKey}:${pinId}`;

        for (const wire of wires) {
            const kFrom = pinKey(wire.from.nodeX, wire.from.nodeY, wire.from.nodeZ, wire.from.pinId);
            const kTo = pinKey(wire.to.nodeX, wire.to.nodeY, wire.to.nodeZ, wire.to.pinId);

            if (kTo === target || kFrom === target) {
                if (wire.state) return true;

                // Checa o nó de origem
                const otherNode = (kTo === target)
                    ? circuit.get(core.key(wire.from.nodeX, wire.from.nodeY, wire.from.nodeZ))
                    : circuit.get(core.key(wire.to.nodeX, wire.to.nodeY, wire.to.nodeZ));

                if (otherNode) {
                    if (otherNode.type === core.COMP.SWITCH && otherNode.state) return true;
                    if (core.BUTTON_TYPES.has(otherNode.type) && otherNode.state) return true;
                    if (otherNode.type === core.COMP.RELAY && otherNode.state) {
                        const otherPin = (kTo === target) ? wire.from.pinId : wire.to.pinId;
                        if (otherPin === 'NO' || otherPin === '14' || otherPin === 'COM') return true;
                    }
                    if (otherNode.type === core.COMP.CONTACTOR_3P && otherNode.state) {
                        return true;
                    }
                    if (otherNode.type === core.COMP.POWER_SUPPLY_3P && (otherNode.manualValue !== false)) {
                        return true;
                    }
                    if (otherNode.outputStates) {
                        const otherPin = (kTo === target) ? wire.from.pinId : wire.to.pinId;
                        if (otherNode.outputStates[otherPin]) return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Verifica se um terminal está conectado a uma referência comum/neutro
     */
    function isTerminalReturnConnected(motorKey, pinId, customWires) {
        const wires = customWires || state.airWires;
        if (!wires) return false;
        const target = `${motorKey}:${pinId}`;

        for (const wire of wires) {
            const kFrom = pinKey(wire.from.nodeX, wire.from.nodeY, wire.from.nodeZ, wire.from.pinId);
            const kTo = pinKey(wire.to.nodeX, wire.to.nodeY, wire.to.nodeZ, wire.to.pinId);
            if (kTo === target || kFrom === target) {
                return true;
            }
        }
        return false;
    }

    /**
     * Cálculo eletrotécnico do Motor Trifásico 6P
     */
    function analyseMotor6P(motor, uf, powerSupply, activeCircuit, activeWires) {
        const mKey = core.key(motor.x, motor.y, motor.z);

        const w2 = `${mKey}:W2`;
        const u2 = `${mKey}:U2`;
        const v2 = `${mKey}:V2`;
        const u1 = `${mKey}:U1`;
        const v1 = `${mKey}:V1`;
        const w1 = `${mKey}:W1`;

        const isStarJoined = uf.connected(w2, u2) && uf.connected(u2, v2);
        const isDeltaJoined = uf.connected(u1, w2) && uf.connected(v1, u2) && uf.connected(w1, v2) &&
                              !uf.connected(u1, v1) && !uf.connected(v1, w1);

        const psKey = powerSupply ? core.key(powerSupply.x, powerSupply.y, powerSupply.z) : null;
        const isPsOn = powerSupply ? (powerSupply.manualValue !== false) : true;
        const srcL1 = psKey ? `${psKey}:L1` : null;
        const srcL2 = psKey ? `${psKey}:L2` : null;
        const srcL3 = psKey ? `${psKey}:L3` : null;
        const srcPE = psKey ? `${psKey}:PE` : null;

        // Aterramento PE: se houver fonte trifásica, checa via barramento de proteção PE; senão fallback
        const isPEConnected = srcPE ? uf.connected(srcPE, `${mKey}:PE`) : isTerminalReturnConnected(mKey, 'PE', activeWires);
        motor.grounded = Boolean(isPEConnected);
        motor.fault = null;

        function getPinPhase(pId) {
            const target = `${mKey}:${pId}`;
            if (powerSupply) {
                if (!isPsOn) return null;
                if (uf.connected(target, srcL1)) return 'L1';
                if (uf.connected(target, srcL2)) return 'L2';
                if (uf.connected(target, srcL3)) return 'L3';
                return null;
            }
            return isTerminalEnergized(mKey, pId, activeWires, activeCircuit) ? 'L1' : null;
        }

        const phU1 = getPinPhase('U1');
        const phV1 = getPinPhase('V1');
        const phW1 = getPinPhase('W1');

        const phW2 = getPinPhase('W2');
        const phU2 = getPinPhase('U2');
        const phV2 = getPinPhase('V2');

        // FECHAMENTO ESTRELA (Y - 380V)
        if (isStarJoined && !uf.connected(w2, u1)) {
            const anyPowered = Boolean(phU1 || phV1 || phW1);

            // Aterramento Obrigatório Punitivo (NR-10 / Segurança)
            if (anyPowered && !motor.grounded) {
                motor.connectionType = 'BLOQUEIO: FALTA DE ATERRAMENTO PE (NR-10)';
                motor.targetRpm = 0;
                motor.direction = 'Bloqueio de Segurança (0 RPM)';
                motor.current = 0.0;
                motor.stalled = false;
                motor.fault = 'Perigo de Choque: Falta de Aterramento de Proteção PE (NR-10). Conecte o borne PE à fonte!';
                triggerPeAlarm(mKey);
                return;
            }

            if (powerSupply) {
                const phases = [phU1, phV1, phW1];
                const activeCount = phases.filter(Boolean).length;
                const distinctPhases = new Set(phases.filter(Boolean));

                if (activeCount === 3 && distinctPhases.size === 3) {
                    // Três fases distintas conectadas (rotação síncrona nominal de 1150 RPM para 6 polos / 60Hz)
                    const isDirect = (phU1 === 'L1' && phV1 === 'L2' && phW1 === 'L3') ||
                                     (phU1 === 'L2' && phV1 === 'L3' && phW1 === 'L1') ||
                                     (phU1 === 'L3' && phV1 === 'L1' && phW1 === 'L2');
                    if (isDirect) {
                        motor.connectionType = 'Estrela (Y - 380V) - Sequência Direta L1-L2-L3';
                        motor.targetRpm = 1150;
                        motor.direction = 'Horário (+1150 RPM)';
                    } else {
                        motor.connectionType = 'Estrela (Y - 380V) - Sequência Inversa (Reversão)';
                        motor.targetRpm = -1150;
                        motor.direction = 'Anti-Horário (-1150 RPM)';
                    }
                    motor.current = 4.8;
                    motor.stalled = false;
                    motor.fault = null;
                } else if (anyPowered) {
                    motor.connectionType = 'FALTA DE FASE EM ESTRELA';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM) / Zumbido';
                    motor.current = 1.8;
                    motor.stalled = true;
                    motor.fault = 'Falta de fase: conecte as 3 fases L1, L2 e L3 aos bornes U1, V1 e W1!';
                } else {
                    motor.connectionType = 'Estrela (Y - 380V) Desenergizada';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 0.0;
                    motor.stalled = false;
                    motor.fault = null;
                }
            } else {
                // Modo legado (sem fonte trifásica dedicada)
                const pU1 = Boolean(phU1), pV1 = Boolean(phV1), pW1 = Boolean(phW1);
                const fullPower = (pU1 && pV1 && pW1) || (pU1 && pV1) || (pV1 && pW1) || (pU1 && pW1);
                if (fullPower) {
                    motor.connectionType = 'Estrela (Y - 380V)';
                    motor.targetRpm = 1150;
                    motor.direction = 'Horário (+1150 RPM)';
                    motor.current = 4.8;
                    motor.stalled = false;
                } else if (anyPowered) {
                    motor.connectionType = 'FALTA DE FASE EM ESTRELA';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 1.2;
                    motor.stalled = false;
                    motor.fault = 'Falta de fase: conecte todas as linhas de alimentação aos bornes U1, V1 e W1!';
                } else {
                    motor.connectionType = 'Estrela (Y - 380V) Desenergizada';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 0.0;
                    motor.stalled = false;
                }
            }
            return;
        }

        // FECHAMENTO TRIÂNGULO (Δ - 220V)
        if (isDeltaJoined) {
            const pair1Phase = phU1 || phW2;
            const pair2Phase = phV1 || phU2;
            const pair3Phase = phW1 || phV2;
            const anyDeltaPowered = Boolean(pair1Phase || pair2Phase || pair3Phase);

            // Aterramento Obrigatório Punitivo (NR-10 / Segurança)
            if (anyDeltaPowered && !motor.grounded) {
                motor.connectionType = 'BLOQUEIO: FALTA DE ATERRAMENTO PE (NR-10)';
                motor.targetRpm = 0;
                motor.direction = 'Bloqueio de Segurança (0 RPM)';
                motor.current = 0.0;
                motor.stalled = false;
                motor.fault = 'Perigo de Choque: Falta de Aterramento de Proteção PE (NR-10). Conecte o borne PE à fonte!';
                triggerPeAlarm(mKey);
                return;
            }

            if (powerSupply) {
                const pairs = [pair1Phase, pair2Phase, pair3Phase];
                const activeCount = pairs.filter(Boolean).length;
                const distinctPhases = new Set(pairs.filter(Boolean));

                if (activeCount === 3 && distinctPhases.size === 3) {
                    const isDirect = (pair1Phase === 'L1' && pair2Phase === 'L2' && pair3Phase === 'L3') ||
                                     (pair1Phase === 'L2' && pair2Phase === 'L3' && pair3Phase === 'L1') ||
                                     (pair1Phase === 'L3' && pair2Phase === 'L1' && pair3Phase === 'L2');
                    if (isDirect) {
                        motor.connectionType = 'Triângulo (Δ - 220V) - Sequência Direta';
                        motor.targetRpm = 1150;
                        motor.direction = 'Horário (+1150 RPM)';
                    } else {
                        motor.connectionType = 'Triângulo (Δ - 220V) - Sequência Inversa (Reversão)';
                        motor.targetRpm = -1150;
                        motor.direction = 'Anti-Horário (-1150 RPM)';
                    }
                    motor.current = 8.3;
                    motor.stalled = false;
                    motor.fault = null;
                } else if (anyDeltaPowered) {
                    motor.connectionType = 'FALTA DE FASE EM TRIÂNGULO';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM) / Zumbido';
                    motor.current = 2.4;
                    motor.stalled = true;
                    motor.fault = 'Falta de fase no fechamento triângulo: verifique os 3 pares U1-W2, V1-U2 e W1-V2!';
                } else {
                    motor.connectionType = 'Triângulo (Δ - 220V) Desenergizada';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 0.0;
                    motor.stalled = false;
                    motor.fault = null;
                }
            } else {
                const p1 = Boolean(pair1Phase), p2 = Boolean(pair2Phase), p3 = Boolean(pair3Phase);
                const fullDelta = p1 && p2 && p3;
                if (fullDelta) {
                    motor.connectionType = 'Triângulo (Δ - 220V)';
                    motor.targetRpm = 1150;
                    motor.direction = 'Horário (+1150 RPM)';
                    motor.current = 8.3;
                    motor.stalled = false;
                } else if (anyDeltaPowered) {
                    motor.connectionType = 'FALTA DE FASE EM TRIÂNGULO';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 2.1;
                    motor.stalled = false;
                    motor.fault = 'Falta de fase no fechamento triângulo: verifique os 3 pares U1-W2, V1-U2 e W1-V2!';
                } else {
                    motor.connectionType = 'Triângulo (Δ - 220V) Desenergizada';
                    motor.targetRpm = 0;
                    motor.direction = 'Parado (0 RPM)';
                    motor.current = 0.0;
                    motor.stalled = false;
                }
            }
            return;
        }

        // Sem fechamento válido
        motor.connectionType = 'Desconectado / Fechamento Incompleto';
        motor.targetRpm = 0;
        motor.direction = 'Parado (0 RPM)';
        motor.current = 0.0;
        motor.stalled = false;
        const anyPin = phU1 || phV1 || phW1 || phW2 || phU2 || phV2;
        if (anyPin) {
            motor.fault = 'Fechamento de placa incorreto: interligue W2-U2-V2 para Estrela (Y) ou U1-W2, V1-U2, W1-V2 para Triângulo (Δ).';
        }
    }

    let _lastPeAlarmTime = 0;
    function triggerPeAlarm(key) {
        const now = Date.now();
        if (now - _lastPeAlarmTime > 4000) {
            _lastPeAlarmTime = now;
            if (window.LogicCraft?.ui?.showToast) {
                window.LogicCraft.ui.showToast('error', 'Falha Crítica NR-10: Bloqueio de partida — Carcaça metálica desprotegida. Conecte o condutor de aterramento PE.');
            }
        }
    }

    /**
     * Cálculo eletrotécnico do Motor Monofásico com Capacitor
     */
    function analyseMotorMono(motor, uf) {
        const mKey = core.key(motor.x, motor.y, motor.z);
        const u1Key = `${mKey}:U1`;
        const u2Key = `${mKey}:U2`;
        const z1Key = `${mKey}:Z1`;
        const z2Key = `${mKey}:Z2`;

        let u1Active = isTerminalEnergized(mKey, 'U1');
        let u2Active = isTerminalEnergized(mKey, 'U2');
        let z1Active = isTerminalEnergized(mKey, 'Z1');
        let z2Active = isTerminalEnergized(mKey, 'Z2');

        let u1Return = isTerminalReturnConnected(mKey, 'U1');
        let u2Return = isTerminalReturnConnected(mKey, 'U2');
        let z1Return = isTerminalReturnConnected(mKey, 'Z1');
        let z2Return = isTerminalReturnConnected(mKey, 'Z2');

        // Propaga energização/retorno através de jumpers/pontes no UnionFind
        if (uf.connected(u1Key, z1Key)) {
            if (u1Active || z1Active) { u1Active = true; z1Active = true; }
            if (u1Return || z1Return) { u1Return = true; z1Return = true; }
        }
        if (uf.connected(u2Key, z2Key)) {
            if (u2Active || z2Active) { u2Active = true; z2Active = true; }
            if (u2Return || z2Return) { u2Return = true; z2Return = true; }
        }
        if (uf.connected(u1Key, z2Key)) {
            if (u1Active || z2Active) { u1Active = true; z2Active = true; }
            if (u1Return || z2Return) { u1Return = true; z2Return = true; }
        }
        if (uf.connected(u2Key, z1Key)) {
            if (u2Active || z1Active) { u2Active = true; z1Active = true; }
            if (u2Return || z1Return) { u2Return = true; z1Return = true; }
        }

        const isPEConnected = isTerminalReturnConnected(mKey, 'PE');
        motor.grounded = Boolean(isPEConnected);
        motor.fault = null;

        // Enrolamento Principal alimentado (U1 e U2)
        const mainPowered = (u1Active && u2Return) || (u2Active && u1Return);

        if (!mainPowered) {
            motor.connectionType = 'Desenergizado';
            motor.targetRpm = 0;
            motor.direction = 'Parado (0 RPM)';
            motor.current = 0.0;
            motor.stalled = false;
            return;
        }

        // Aterramento Obrigatório Punitivo (NR-10 / Segurança)
        if (!motor.grounded) {
            motor.connectionType = 'BLOQUEIO: FALTA DE ATERRAMENTO PE (NR-10)';
            motor.targetRpm = 0;
            motor.direction = 'Bloqueio de Segurança (0 RPM)';
            motor.current = 0.0;
            motor.stalled = false;
            motor.fault = 'Perigo de Choque: Falta de Aterramento de Proteção PE (NR-10)';
            triggerPeAlarm(mKey);
            return;
        }

        // Enrolamento Auxiliar com Capacitor:
        // Sentido Horário: U1 e Z1 na Linha/Fase, U2 e Z2 no Retorno/Neutro
        const isCW = (u1Active && z1Active && (u2Return || z2Return)) || 
                     (u2Active && z2Active && (u1Return || z1Return));

        // Sentido Anti-Horário (Invertido): U1 e Z2 na Linha/Fase, U2 e Z1 no Retorno/Neutro
        const isCCW = (u1Active && z2Active && (u2Return || z1Return)) || 
                      (u2Active && z1Active && (u1Return || z2Return));

        if (isCW) {
            motor.connectionType = 'Monofásico 220V (Horário)';
            motor.targetRpm = 1740;
            motor.direction = 'Horário (+1740 RPM)';
            motor.current = 6.4;
            motor.stalled = false;
        } else if (isCCW) {
            motor.connectionType = 'Monofásico 220V (Anti-Horário / Invertido)';
            motor.targetRpm = -1740;
            motor.direction = 'Anti-Horário (-1740 RPM)';
            motor.current = 6.4;
            motor.stalled = false;
        } else {
            // Modo Didático de Falha: Enrolamento principal alimentado sem auxiliar/capacitor (STALL)
            motor.connectionType = 'ROTOR BLOQUEADO (Sem Partida Auxiliar)';
            motor.targetRpm = 0;
            motor.direction = 'Vibração / Stall (0 RPM)';
            motor.current = 14.8;
            motor.stalled = true;
            motor.fault = 'ROTOR BLOQUEADO: Motor monofásico não parte sem o enrolamento auxiliar Z1/Z2 conectado!';
        }
    }

    /**
     * Atualização contínua de inércia, rotação, desaceleração por contracorrente e modelo térmico
     */
    function updateMachines(dt, circuitMap) {
        let needFeedback = false;
        const safeDt = Math.min(dt || 0.016, 0.05);
        const activeCircuit = circuitMap || state.circuit;
        if (!activeCircuit) return false;

        let anyMotorRunning = false;
        activeCircuit.forEach(node => {
            if (node.type === core.COMP.MOTOR_INDUCTION_6P || node.type === core.COMP.MOTOR_INDUCTION_MONO) {
                let currentRpm = node.rpm || 0;
                const target = node.targetRpm || 0;

                // Rampa Inercial de Frenagem em Reversão de Fase (Contracorrente até 0 RPM)
                if (target !== 0 && currentRpm !== 0 && Math.sign(target) !== Math.sign(currentRpm)) {
                    const brakeDecel = 1600 * safeDt;
                    if (currentRpm > 0) {
                        currentRpm = Math.max(0, currentRpm - brakeDecel);
                    } else {
                        currentRpm = Math.min(0, currentRpm + brakeDecel);
                    }
                    node.rpm = currentRpm;
                } else {
                    const accelRate = (target === 0) ? 0.9 : 2.5;
                    node.rpm = THREE.MathUtils.lerp(currentRpm, target, safeDt * accelRate);
                    if (Math.abs(node.rpm) < 1.0 && target === 0) node.rpm = 0;
                }

                if (Math.abs(node.rpm) > 25) {
                    anyMotorRunning = true;
                }

                // Normalização do Modelo Térmico Bimetálico (tau = 8.0s)
                const ratedCurrent = (node.type === core.COMP.MOTOR_INDUCTION_6P) ? 5.0 : 6.4;
                const motorCurrent = (node.current || 0);

                if (window.LogicCraftGuard?.computeThermalStep) {
                    node.thermalEnergy = window.LogicCraftGuard.computeThermalStep(
                        motorCurrent,
                        ratedCurrent,
                        node.thermalEnergy || 0,
                        safeDt,
                        8.0
                    );
                } else {
                    const tau = 8.0;
                    const targetEnergy = Math.pow(motorCurrent / ratedCurrent, 2);
                    node.thermalEnergy = (node.thermalEnergy || 0) + (safeDt / tau) * (targetEnergy - (node.thermalEnergy || 0));
                }

                // Disparo Térmico em Sobrecarga prolongada ou Stall (Rotor Bloqueado)
                if (node.thermalEnergy >= 2.2) {
                    if (!node.thermalTripped) {
                        node.thermalTripped = true;
                        window.LogicCraft?.audio?.playThermalTrip();
                        if (window.LogicCraft?.ui?.showToast) {
                            window.LogicCraft.ui.showToast('error', '🔥 Proteção Térmica Atuada: Sobrecarga térmica bimetálica! Motor desarmado.');
                        }
                    }
                    node.targetRpm = 0;
                    node.current = 0.0;
                    node.stalled = false;
                } else if (node.thermalEnergy <= 0.4 && node.thermalTripped) {
                    node.thermalTripped = false;
                }

                // Escala de rotação visual ergonômica (elimina aliasing estroboscópico a 60Hz):
                // A 1740 RPM, a velocidade visual é ~19 rad/s (~3 rev/s = ~18° por quadro a 60 FPS), garantindo movimento fluido e dinâmico perceptível
                const maxVisualAngSpeed = 19.0;
                const rpmRatio = Math.min(1.0, Math.abs(node.rpm) / 1740);
                const visualAngSpeed = Math.sign(node.rpm) * (rpmRatio * maxVisualAngSpeed);

                // Animação do eixo dianteiro e do conjunto de hélice/ponteiro de rotação
                if (node.mesh?.userData?.shaft) {
                    node.mesh.userData.shaft.rotation.x += visualAngSpeed * safeDt;

                    // Trepidação visual se estiver em Stall (rotor bloqueado)
                    if (node.stalled) {
                        node.mesh.userData.shaft.rotation.x += (Math.random() - 0.5) * 0.20;
                    }
                }

                // Animação da ventoinha de refrigeração traseira
                if (node.mesh?.userData?.fan) {
                    node.mesh.userData.fan.rotation.x += visualAngSpeed * safeDt;

                    if (node.stalled) {
                        node.mesh.userData.fan.rotation.x += (Math.random() - 0.5) * 0.20;
                    }
                }
            }
        });

        window.LogicCraft?.audio?.setMotorRunning(anyMotorRunning);
        return needFeedback;
    }

    function updateMachineVisuals() {
        // Sem elementos móveis secundários externos; rotação tratada no updateMachines
    }

    // =========================================================================
    // 3D PROCEDURAL MESH BUILDERS (Three.js) - Modelos Industriais Limpos
    // =========================================================================

    function buildMachineMesh(data, helpers) {
        if (!isMachineType(data.type)) return null;

        const createPinLabel = helpers.createPinLabel;
        const pinHitboxGeo = helpers.pinHitboxGeo;
        const pinHitboxMat = helpers.pinHitboxMat;
        // Fallback defensivo contra TypeError:
        const createBoxHighlightMesh = helpers.createBoxHighlightMesh || function() {
            const dummy = new THREE.Group();
            dummy.name = 'focusHighlight';
            dummy.visible = false;
            return dummy;
        };
        const root = new THREE.Group();
        root.userData.outputMeshes = {};

        const isMono = (data.type === core.COMP.MOTOR_INDUCTION_MONO);

        const matStator = new THREE.MeshStandardMaterial({
            color: isMono ? COLOR_STATOR_MONO : COLOR_STATOR_TRI,
            metalness: 0.65,
            roughness: 0.38
        });

        const matFin = new THREE.MeshStandardMaterial({
            color: isMono ? 0x0F172A : 0x1E293B,
            metalness: 0.80,
            roughness: 0.32
        });

        const matChrome = new THREE.MeshStandardMaterial({
            color: COLOR_STEEL,
            metalness: 0.98,
            roughness: 0.08
        });

        const matBrass = new THREE.MeshStandardMaterial({
            color: COLOR_BRASS,
            metalness: 0.90,
            roughness: 0.22
        });

        const matBakelite = new THREE.MeshStandardMaterial({
            color: COLOR_BAKELITE,
            roughness: 0.75,
            metalness: 0.10
        });

        const matDarkMetal = new THREE.MeshStandardMaterial({
            color: 0x1E293B,
            metalness: 0.70,
            roughness: 0.35
        });

        // 1. CARCAÇA CILÍNDRICA HORIZONTAL (Eixo X)
        const statorRadius = isMono ? 0.32 : 0.38;
        const statorLength = isMono ? 0.85 : 1.05;

        const stator = new THREE.Mesh(
            new THREE.CylinderGeometry(statorRadius, statorRadius, statorLength, 28),
            matStator
        );
        stator.rotation.z = Math.PI / 2;
        stator.position.set(0, 0, 0);
        stator.castShadow = true;
        stator.userData = { isDecorative: true, isCasing: true };
        root.add(stator);

        // 2. ALETAS DE RESFRIAMENTO EM ANÉIS CIRCUNFERENCIAIS REGULARES
        const finRadius = statorRadius + 0.045;
        const numRings = isMono ? 7 : 9;
        const finSpacing = (statorLength * 0.78) / (numRings - 1);
        const finStart = -(statorLength * 0.39);

        for (let i = 0; i < numRings; i++) {
            const finRing = new THREE.Mesh(
                new THREE.CylinderGeometry(finRadius, finRadius, 0.024, 28),
                matFin
            );
            finRing.rotation.z = Math.PI / 2;
            finRing.position.set(finStart + i * finSpacing, 0, 0);
            finRing.userData = { isDecorative: true, isCasing: true };
            root.add(finRing);
        }

        // 3. PÉS DE APOIO ROBUSTOS NA BANCADA
        const footGeo = new THREE.BoxGeometry(0.12, 0.12, statorRadius * 2.2);
        const foot1 = new THREE.Mesh(footGeo, matDarkMetal);
        foot1.position.set(-statorLength * 0.32, -0.42, 0);
        foot1.castShadow = true;
        root.add(foot1);

        const foot2 = new THREE.Mesh(footGeo, matDarkMetal);
        foot2.position.set(statorLength * 0.32, -0.42, 0);
        foot2.castShadow = true;
        root.add(foot2);

        // 4. DEFLETOR TRASEIRO E FLANGE DIANTEIRA
        const cowlRear = new THREE.Mesh(
            new THREE.CylinderGeometry(statorRadius * 0.96, statorRadius * 0.96, 0.18, 24),
            new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85, metalness: 0.3 })
        );
        cowlRear.rotation.z = Math.PI / 2;
        cowlRear.position.set(-(statorLength / 2 + 0.09), 0, 0);
        root.add(cowlRear);

        // Ventoinha traseira de refrigeração industrial com pás radiais
        const fanGroup = new THREE.Group();
        fanGroup.position.set(-(statorLength / 2 + 0.12), 0, 0);

        const fanHub = new THREE.Mesh(
            new THREE.CylinderGeometry(0.10, 0.10, 0.08, 16),
            new THREE.MeshStandardMaterial({ color: 0xDC2626, roughness: 0.45, metalness: 0.2 })
        );
        fanHub.rotation.z = Math.PI / 2;
        fanGroup.add(fanHub);

        for (let i = 0; i < 8; i++) {
            const blade = new THREE.Mesh(
                new THREE.BoxGeometry(0.04, statorRadius * 0.70, 0.08),
                new THREE.MeshStandardMaterial({ color: 0xEF4444, roughness: 0.4, metalness: 0.1 })
            );
            const bladeAngle = (i / 8) * Math.PI * 2;
            blade.position.set(0, Math.sin(bladeAngle) * (statorRadius * 0.45), Math.cos(bladeAngle) * (statorRadius * 0.45));
            blade.rotation.x = bladeAngle + 0.35;
            fanGroup.add(blade);
        }
        root.add(fanGroup);
        root.userData.fan = fanGroup;

        const flangeFront = new THREE.Mesh(
            new THREE.CylinderGeometry(statorRadius * 0.98, statorRadius * 0.98, 0.08, 24),
            matDarkMetal
        );
        flangeFront.rotation.z = Math.PI / 2;
        flangeFront.position.set(statorLength / 2 + 0.04, 0, 0);
        root.add(flangeFront);

        // 5. EIXO DIANTEIRO CROMADO COM POLIA INDUSTRIAL DE ALTA VISIBILIDADE (Gira a 1750 RPM)
        const shaftGroup = new THREE.Group();
        shaftGroup.position.set(statorLength / 2 + 0.08, 0, 0);

        const shaftLen = 0.52;
        const shaftRod = new THREE.Mesh(
            new THREE.CylinderGeometry(0.08, 0.08, shaftLen, 24),
            matChrome
        );
        shaftRod.rotation.z = Math.PI / 2;
        shaftRod.position.set(shaftLen / 2, 0, 0);
        shaftGroup.add(shaftRod);

        // Chaveta retangular saliente
        const keyMesh = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.035, 0.035),
            new THREE.MeshStandardMaterial({ color: 0x94A3B8, metalness: 0.92, roughness: 0.2 })
        );
        keyMesh.position.set(shaftLen * 0.4, 0.088, 0);
        shaftGroup.add(keyMesh);

        // --- CONJUNTO ROTATIVO DE ALTA VISIBILIDADE: DISCO ESTROBOSCÓPICO TACÔMETRO + HÉLICE INDUSTRIAL ---
        const rotIndicatorGroup = new THREE.Group();
        rotIndicatorGroup.position.set(shaftLen * 0.72, 0, 0);

        // 1. Disco Estroboscópico / Tacômetro Zebrado de Alta Resolução (512x512)
        const cvTacho = document.createElement('canvas');
        cvTacho.width = 512; cvTacho.height = 512;
        const ctxT = cvTacho.getContext('2d');
        const cx = 256, cy = 256, r = 245;

        // Fundo circular cortado
        ctxT.save();
        ctxT.beginPath();
        ctxT.arc(cx, cy, r, 0, Math.PI * 2);
        ctxT.clip();

        // 4 Quadrantes de Alto Contraste (2x Amarelo Segurança #FACC15 e 2x Preto Grafite #0F172A)
        const quadColors = ['#FACC15', '#0F172A', '#FACC15', '#0F172A'];
        for (let q = 0; q < 4; q++) {
            ctxT.beginPath();
            ctxT.moveTo(cx, cy);
            ctxT.arc(cx, cy, r, q * Math.PI / 2, (q + 1) * Math.PI / 2);
            ctxT.closePath();
            ctxT.fillStyle = quadColors[q];
            ctxT.fill();
        }

        // Anel de Escala Graduada Externa
        ctxT.beginPath();
        ctxT.arc(cx, cy, r - 10, 0, Math.PI * 2);
        ctxT.lineWidth = 14;
        ctxT.strokeStyle = '#FFFFFF';
        ctxT.stroke();

        // Marcas de Grau do Tacômetro
        for (let deg = 0; deg < 360; deg += 15) {
            const rad = deg * Math.PI / 180;
            const innerR = (deg % 90 === 0) ? r - 45 : (deg % 45 === 0 ? r - 35 : r - 25);
            ctxT.beginPath();
            ctxT.moveTo(cx + Math.cos(rad) * innerR, cy + Math.sin(rad) * innerR);
            ctxT.lineTo(cx + Math.cos(rad) * (r - 12), cy + Math.sin(rad) * (r - 12));
            ctxT.lineWidth = (deg % 45 === 0) ? 6 : 3;
            ctxT.strokeStyle = '#FFFFFF';
            ctxT.stroke();
        }

        // Seta Radial Indicadora de Sentido de Giro (Vermelho Fluorescente #EF4444)
        ctxT.fillStyle = '#EF4444';
        ctxT.beginPath();
        ctxT.moveTo(cx, cy - 22);
        ctxT.lineTo(cx + 175, cy - 22);
        ctxT.lineTo(cx + 175, cy - 50);
        ctxT.lineTo(cx + 235, cy);
        ctxT.lineTo(cx + 175, cy + 50);
        ctxT.lineTo(cx + 175, cy + 22);
        ctxT.lineTo(cx, cy + 22);
        ctxT.closePath();
        ctxT.fill();
        ctxT.strokeStyle = '#FFFFFF';
        ctxT.lineWidth = 5;
        ctxT.stroke();

        // Centro do Cubo Metálico
        ctxT.beginPath();
        ctxT.arc(cx, cy, 38, 0, Math.PI * 2);
        ctxT.fillStyle = '#E2E8F0';
        ctxT.fill();
        ctxT.strokeStyle = '#0F172A';
        ctxT.lineWidth = 6;
        ctxT.stroke();

        ctxT.beginPath();
        ctxT.arc(cx, cy, 16, 0, Math.PI * 2);
        ctxT.fillStyle = '#0F172A';
        ctxT.fill();
        ctxT.restore();

        const tachoTex = new THREE.CanvasTexture(cvTacho);
        tachoTex.minFilter = THREE.LinearFilter;

        // Face dianteira do disco estroboscópico de alta legibilidade
        const tachoPlateMat = new THREE.MeshStandardMaterial({
            map: tachoTex,
            roughness: 0.35,
            metalness: 0.2
        });

        const tachoDisc = new THREE.Mesh(
            new THREE.CylinderGeometry(0.32, 0.32, 0.035, 32),
            [
                new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.3 }),
                tachoPlateMat, // Face superior / dianteira
                new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.8 })
            ]
        );
        tachoDisc.rotation.z = -Math.PI / 2;
        tachoDisc.position.set(0.06, 0, 0);
        rotIndicatorGroup.add(tachoDisc);

        // 2. 4 Pás Aerodinâmicas Largas com Cores Alternadas (Amarelo Segurança e Preto Industrial)
        const matBladeYellow = new THREE.MeshStandardMaterial({ color: 0xFACC15, metalness: 0.2, roughness: 0.35 });
        const matBladeDark   = new THREE.MeshStandardMaterial({ color: 0x0F172A, metalness: 0.4, roughness: 0.6 });
        const bladeGeo = new THREE.BoxGeometry(0.028, 0.30, 0.13);
        for (let b = 0; b < 4; b++) {
            const isYellow = (b % 2 === 0);
            const blade = new THREE.Mesh(bladeGeo, isYellow ? matBladeYellow : matBladeDark);
            const bladeAngle = (b / 4) * Math.PI * 2;
            const dist = 0.20;
            blade.position.set(-0.02, Math.sin(bladeAngle) * dist, Math.cos(bladeAngle) * dist);
            blade.rotation.x = bladeAngle + 0.40;
            rotIndicatorGroup.add(blade);
        }

        // 3. Ogiva / Cúpula dianteira de fixação central do eixo
        const spinnerCone = new THREE.Mesh(
            new THREE.ConeGeometry(0.09, 0.12, 24),
            new THREE.MeshStandardMaterial({ color: 0xEF4444, metalness: 0.7, roughness: 0.2 })
        );
        spinnerCone.rotation.z = -Math.PI / 2;
        spinnerCone.position.set(0.12, 0, 0);
        rotIndicatorGroup.add(spinnerCone);

        shaftGroup.add(rotIndicatorGroup);

        root.add(shaftGroup);
        root.userData.shaft = shaftGroup;
        root.userData.rotIndicator = rotIndicatorGroup;

        // 6. CAPACITOR DE PARTIDA FÍSICO VISÍVEL (Apenas no Motor Monofásico)
        // Dimensões industriais: diâmetro 0.35 (raio 0.175), comprimento 0.88
        if (isMono) {
            const capGroup = new THREE.Group();
            capGroup.position.set(0, 0.22, -0.36);

            const capBody = new THREE.Mesh(
                new THREE.CylinderGeometry(0.175, 0.175, 0.88, 28),
                new THREE.MeshStandardMaterial({ color: 0xE2E8F0, metalness: 0.96, roughness: 0.12 })
            );
            capBody.rotation.z = Math.PI / 2;
            capBody.castShadow = true;
            capGroup.add(capBody);

            // Abraçadeiras pretas de fixação mecânica ao estator
            [-0.26, 0.26].forEach(cx => {
                const clamp = new THREE.Mesh(
                    new THREE.BoxGeometry(0.045, 0.36, 0.36),
                    new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.9 })
                );
                clamp.position.set(cx, 0, 0);
                capGroup.add(clamp);
            });

            // Tampa fenólica terminal do capacitor
            const capHead = new THREE.Mesh(
                new THREE.CylinderGeometry(0.16, 0.16, 0.05, 24),
                new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.85 })
            );
            capHead.rotation.z = Math.PI / 2;
            capHead.position.set(0.45, 0, 0);
            capGroup.add(capHead);

            // Terminais de conexão do capacitor
            [-0.05, 0.05].forEach(tz => {
                const stud = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.02, 0.02, 0.04, 12),
                    matBrass
                );
                stud.rotation.z = Math.PI / 2;
                stud.position.set(0.48, 0, tz);
                capGroup.add(stud);
            });

            // Cabo flexível que conecta o capacitor à caixa de bornes do enrolamento auxiliar
            const curve = new THREE.CubicBezierCurve3(
                new THREE.Vector3(0.48, 0, 0),
                new THREE.Vector3(0.38, 0.16, 0.12),
                new THREE.Vector3(0.22, 0.22, 0.24),
                new THREE.Vector3(0.10, 0.22, 0.34)
            );
            const wireCap = new THREE.Mesh(
                new THREE.TubeGeometry(curve, 16, 0.02, 8, false),
                new THREE.MeshStandardMaterial({ color: 0x090D16, roughness: 0.85, metalness: 0.1 })
            );
            capGroup.add(wireCap);

            root.add(capGroup);
        }

        // 7. CAIXA DE BORNES DIDÁTICA SUPERIOR TOTALMENTE ACESSÍVEL (Y = +0.65)
        const tboxWidth = isMono ? 0.60 : 0.72;
        const tboxDepth = isMono ? 0.48 : 0.54;

        // Base da caixa
        const tboxBase = new THREE.Mesh(
            new THREE.BoxGeometry(tboxWidth, 0.14, tboxDepth),
            new THREE.MeshStandardMaterial({ color: 0x1E293B, roughness: 0.85 })
        );
        tboxBase.position.set(0, 0.44, 0);
        tboxBase.userData = { isDecorative: true, isCasing: true };
        root.add(tboxBase);

        // Placa de baquelite isolante laranja/marrom
        const tplate = new THREE.Mesh(
            new THREE.BoxGeometry(tboxWidth - 0.06, 0.04, tboxDepth - 0.06),
            matBakelite
        );
        tplate.position.set(0, 0.52, 0);
        tplate.userData = { isDecorative: true, isCasing: true };
        root.add(tplate);

        // BORNES DE LATÃO SEXTAVADOS COM HITBOX CALIBRADA (0.09) E RELEVO DESTACADO (+0.08)
        const localPins = core.getLocalComponentPins(data.type);
        const allPins = [...localPins.inputs, ...localPins.outputs];

        root.userData.pinElements = root.userData.pinElements || {};

        // Geometria precisa e não-sobreposta para bornes com espaçamento de 0.20m (raio estrito de 0.09)
        const motorPinHitboxGeo = new THREE.CylinderGeometry(0.09, 0.09, 0.22, 16);

        allPins.forEach(pin => {
            const px = pin.x;
            const pz = pin.z;
            const py = pin.y || 0.65;

            const isPE = (pin.id === 'PE');
            const pinUserData = {
                isPin: true,
                pinType: 'input',
                pinId: pin.id,
                localX: px,
                localY: py,
                localZ: pz
            };

            // Ilha metálica elevada dourada / verde para PE (+0.02 acima da placa)
            const brassPad = new THREE.Mesh(
                new THREE.CylinderGeometry(0.065, 0.07, 0.03, 16),
                isPE ? new THREE.MeshStandardMaterial({ color: 0x15803D, metalness: 0.6, roughness: 0.3 }) : matBrass
            );
            brassPad.position.set(px, 0.56, pz);
            brassPad.userData = pinUserData;
            root.add(brassPad);

            // Borne de latão sextavado saliente (verde para PE) destacado em relevo (+0.08)
            const hexPost = new THREE.Mesh(
                new THREE.CylinderGeometry(0.042, 0.042, 0.09, 6),
                isPE ? new THREE.MeshStandardMaterial({ color: 0x22C55E, metalness: 0.8, roughness: 0.2 }) : matBrass
            );
            hexPost.position.set(px, 0.62, pz);
            hexPost.userData = pinUserData;
            root.add(hexPost);

            // Cabeça do parafuso central de aperto em cromo polido
            const screw = new THREE.Mesh(
                new THREE.CylinderGeometry(0.025, 0.025, 0.02, 12),
                matChrome
            );
            screw.position.set(px, 0.675, pz);
            screw.userData = pinUserData;
            root.add(screw);

            // Hitbox precisa e calibrada estritamente em raio 0.09 para impedir qualquer sobreposição espacial
            const pHitbox = new THREE.Mesh(motorPinHitboxGeo, pinHitboxMat);
            pHitbox.position.set(px, py, pz);
            pHitbox.userData = pinUserData;
            root.add(pHitbox);

            // Etiqueta serigráfica nítida (W2, U2, V2, U1, V1, W1 ou U1, U2, Z1, Z2)
            const lbl = createPinLabel(pin.id, true);
            lbl.position.set(px, py + 0.09, pz - 0.06);
            lbl.scale.set(0.65, 0.65, 0.65);
            lbl.userData = pinUserData;
            root.add(lbl);

            root.userData.pinElements[pin.id] = { post: hexPost, hitbox: pHitbox, label: lbl };
        });

        // Borda de foco da bancada
        root.add(createBoxHighlightMesh(statorLength + 0.35, statorRadius * 2.5, 0));

        root.rotation.y = data.rot * (Math.PI / 2);
        return root;
    }

    return {
        isMachineType,
        solvePowerCircuit,
        updateMachines,
        updateMachineVisuals,
        buildMachineMesh
    };
})();
